import { useState, useEffect } from 'react'
import { Search, FileText, Edit, Trash2, RefreshCw, Eye } from 'lucide-react'
import { usePermissions } from '@/hooks/usePermissions'
import { Navigate } from 'react-router-dom'
import postsService, { Post, CreatePostInput, UpdatePostInput } from '@/services/postsService'
import { PostFormModal } from '@/components/PostFormModal'
import { DeletePostModal } from '@/components/DeletePostModal'
import { PostDetailsModal } from '@/components/PostDetailsModal'

export default function Content() {
  const { canManageAnyContent, user } = usePermissions()
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedPost, setSelectedPost] = useState<Post | null>(null)
  const [activeTab, setActiveTab] = useState<'published' | 'draft' | 'archived'>('published')

  const isCreator = user?.role === 'creator'

  // Force creators to always see only the drafts tab
  useEffect(() => {
    if (isCreator) setActiveTab('draft')
  }, [isCreator])

  // Guard - Redireciona se não tiver permissão
  if (!canManageAnyContent()) {
    return <Navigate to="/" replace />
  }

  // Verifica se o usuário pode editar um post específico
  const canEditPost = (post: Post): boolean => {
    if (!user) return false
    // god/admin can edit any post
    if (user.role === 'god' || user.role === 'admin') {
      return true
    }
    // creator can only edit their own drafts
    if (user.role === 'creator') {
      return post.author_id === user.id && post.status !== 'published'
    }
    return false
  }

  // Verifica se o usuário pode deletar um post específico
  const canDeletePost = (post: Post): boolean => {
    if (!user) return false
    if (user.role === 'god' || user.role === 'admin') return true
    if (user.role === 'creator') {
      return post.author_id === user.id && post.status === 'draft'
    }
    return false
  }

  // Carregar posts
  const loadPosts = async () => {
    try {
      setLoading(true)
      const response = await postsService.listPosts({
        page,
        limit: 20,
        search,
        status: activeTab,
        ...(isCreator && { author_id: user?.id })
      })
      setPosts(response.data)
      setTotalPages(response.pagination.total_pages)
    } catch (error) {
      console.error('Erro ao carregar posts:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setPage(1)
  }, [activeTab])

  useEffect(() => {
    loadPosts()
  }, [page, search, activeTab])

  // Criar post
  const handleCreate = async (data: CreatePostInput) => {
    await postsService.createPost(data)
    setShowCreateModal(false)
    loadPosts()
  }

  // Abrir modal de edição (busca post completo primeiro)
  const handleEditClick = async (postId: string) => {
    try {
      const fullPost = await postsService.getPostById(postId)
      setSelectedPost(fullPost)
      setShowEditModal(true)
    } catch (error) {
      console.error('Erro ao carregar post:', error)
    }
  }

  // Editar post
  const handleEdit = async (data: UpdatePostInput) => {
    if (!selectedPost) return
    await postsService.updatePost(selectedPost.id, data)
    setShowEditModal(false)
    setSelectedPost(null)
    loadPosts()
  }

  // Deletar post
  const handleDelete = async () => {
    if (!selectedPost) return
    await postsService.deletePost(selectedPost.id)
    setShowDeleteModal(false)
    setSelectedPost(null)
    loadPosts()
  }

  // Reativar post arquivado (volta para rascunho)
  const handleRestore = async (post: Post) => {
    try {
      await postsService.updatePost(post.id, { status: 'draft' })
      loadPosts()
    } catch (error) {
      console.error('Erro ao reativar post:', error)
    }
  }

  const getStatusBadge = (status: string) => {
    const colors = {
      published: 'bg-green-100 text-green-800',
      draft: 'bg-yellow-100 text-yellow-800',
      archived: 'bg-gray-100 text-gray-800',
    }
    return colors[status as keyof typeof colors] || colors.draft
  }

  const getStatusLabel = (status: string) => {
    const labels = {
      published: 'Publicado',
      draft: 'Rascunho',
      archived: 'Arquivado',
    }
    return labels[status as keyof typeof labels] || status
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciamento de Posts</h1>
          <p className="text-sm text-gray-600 mt-1">Gerencie todos os posts do sistema</p>
        </div>
        {activeTab !== 'archived' && (
          <button onClick={() => setShowCreateModal(true)} className="btn btn-primary w-full sm:w-auto">
            <FileText className="mr-2 h-4 w-4" />
            Novo Post
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max sm:min-w-0">
          {!isCreator && (
            <button
              onClick={() => setActiveTab('published')}
              className={`${activeTab === 'published'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } whitespace-nowrap border-b-2 py-3 sm:py-4 px-2 sm:px-1 text-sm font-medium`}
            >
              Publicados
            </button>
          )}
          <button
            onClick={() => setActiveTab('draft')}
            className={`${activeTab === 'draft'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap border-b-2 py-3 sm:py-4 px-2 sm:px-1 text-sm font-medium`}
          >
            Rascunhos
          </button>
          {!isCreator && (
            <button
              onClick={() => setActiveTab('archived')}
              className={`${activeTab === 'archived'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                } whitespace-nowrap border-b-2 py-3 sm:py-4 px-2 sm:px-1 text-sm font-medium`}
            >
              Arquivados
            </button>
          )}
        </nav>
      </div>

      {/* Search and Table */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar posts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Posts Table */}
        {loading ? (
          <div className="text-center py-12">Carregando...</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>Nenhum post encontrado</p>
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="block lg:hidden space-y-4">
              {posts.map((post) => (
                <div key={post.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-900 truncate">{post.title}</h3>
                      {post.summary && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">{post.summary}</p>
                      )}
                    </div>
                    <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(post.status)} whitespace-nowrap`}>
                      {getStatusLabel(post.status)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{post.view_count} visualizações</span>
                    <span>{new Date(post.created_at).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    {activeTab !== 'archived' ? (
                      <>
                        <button
                          onClick={() => {
                            setSelectedPost(post)
                            setShowDetailsModal(true)
                          }}
                          className="flex-1 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-md border border-gray-200 flex items-center justify-center gap-1"
                        >
                          <Eye className="h-4 w-4" />
                          Ver
                        </button>
                        {canEditPost(post) && (
                          <button
                            onClick={() => handleEditClick(post.id)}
                            className="flex-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md border border-blue-200 flex items-center justify-center gap-1"
                          >
                            <Edit className="h-4 w-4" />
                            Editar
                          </button>
                        )}
                        {canDeletePost(post) && (
                          <button
                            onClick={() => {
                              setSelectedPost(post)
                              setShowDeleteModal(true)
                            }}
                            className="flex-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md border border-red-200 flex items-center justify-center gap-1"
                          >
                            <Trash2 className="h-4 w-4" />
                            Deletar
                          </button>
                        )}
                      </>
                    ) : (
                      <button
                        onClick={() => handleRestore(post)}
                        className="w-full px-3 py-2 text-sm text-green-600 hover:bg-green-50 rounded-md border border-green-200 flex items-center justify-center gap-1"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Reativar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Visualizações</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Criado em</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {posts.map((post) => (
                    <tr key={post.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => { handleEditClick(post.id); setShowDetailsModal(true) }}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 max-w-md truncate">
                          {post.title}
                        </div>
                        {post.summary && (
                          <div className="text-xs text-gray-500 max-w-md truncate">
                            {post.summary}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 font-mono">{post.slug}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(post.status)}`}>
                          {getStatusLabel(post.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {post.view_count}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(post.created_at).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                        {activeTab !== 'archived' ? (
                          <div className="flex items-center justify-end gap-2">
                            {canEditPost(post) && (
                              <button
                                onClick={() => handleEditClick(post.id)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Editar"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {canDeletePost(post) && (
                              <button
                                onClick={() => {
                                  setSelectedPost(post)
                                  setShowDeleteModal(true)
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="Deletar"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={() => handleRestore(post)}
                            className="text-green-600 hover:text-green-900 flex items-center"
                            title="Reativar post"
                          >
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Reativar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-0 sm:space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="w-full sm:w-auto px-4 py-2 border rounded disabled:opacity-50"
                >
                  Anterior
                </button>
                <span className="px-4 py-2 text-sm">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="w-full sm:w-auto px-4 py-2 border rounded disabled:opacity-50"
                >
                  Próxima
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <PostFormModal
          title="Criar Post"
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
          userRole={user?.role}
        />
      )}

      {showEditModal && selectedPost && (
        <PostFormModal
          title="Editar Post"
          post={selectedPost}
          onClose={() => {
            setShowEditModal(false)
            setSelectedPost(null)
          }}
          onSubmit={handleEdit}
          userRole={user?.role}
        />
      )}

      {showDeleteModal && selectedPost && (
        <DeletePostModal
          postTitle={selectedPost.title}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedPost(null)
          }}
          onConfirm={handleDelete}
        />
      )}

      <PostDetailsModal
        isOpen={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); setSelectedPost(null) }}
        post={selectedPost}
      />
    </div>
  )
}
